import React, { useState, useEffect, useContext } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import {
  Bell,
  Send,
  Users,
  Megaphone,
  Plus,
  Edit2,
  Trash2,
  Calendar,
  Clock,
  AlertCircle,
  Building,
  Globe,
  Filter,
  X,
  ExternalLink,
  Eye,
} from "lucide-react";
import {
  Notice,
  CreateNoticeRequest,
  getAllNotices,
  createNotice,
  deleteNotice,
  updateNotice,
  markNoticeAsRead,
} from "../../services/notices";
import { AuthContext } from "../../contexts/AuthContext";
import { toast } from "react-hot-toast";
import Pagination from "../../components/common/Pagination";

const AdminAnnouncements: React.FC = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState<Notice | null>(null);
  const [filterType, setFilterType] = useState<string>("all");
  const [filterAudience, setFilterAudience] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };
  const [formData, setFormData] = useState<CreateNoticeRequest>({
    title: "",
    content: "",
    type: "general",
    priority: "medium",
    targetAudience: ["all"],
    publishDate: new Date().toISOString().split("T")[0],
    expiryDate: "",
  });

  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;
  const queryClient = useQueryClient();

  // Admins can edit all notices
  const canEditNotice = (notice: Notice): boolean => {
    return true; // Admins can edit all announcements
  };

  // Get all notices (admins can see all notices)
  const { data: noticesResponse, isLoading } = useQuery({
    queryKey: ["notices", currentPage, pageSize, filterType, filterAudience],
    queryFn: () =>
      getAllNotices({
        page: currentPage,
        limit: pageSize,
        type: filterType !== "all" ? filterType : undefined,
        targetAudience: filterAudience !== "all" ? filterAudience : undefined,
      }),
  });

  const notices = noticesResponse?.data || [];
  const pagination = noticesResponse?.pagination;

  // Filter out notices with invalid data
  const validNotices = notices.filter(
    (notice: any) =>
      notice &&
      notice.author &&
      notice.author.userId &&
      (typeof notice.author.userId === "string" || notice.author.userId._id),
  );

  // Filter notices based on selected filters (client-side filtering for display only)
  const filteredNotices = validNotices;

  // Create notice mutation
  const createNoticeMutation = useMutation({
    mutationFn: createNotice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notices"] });
      setShowCreateModal(false);
      resetForm();
      toast.success("Announcement created successfully!");
    },
    onError: () => {
      toast.error("Failed to create announcement");
    },
  });

  // Update notice mutation
  const updateNoticeMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateNoticeRequest }) =>
      updateNotice(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notices"] });
      setShowEditModal(false);
      setSelectedNotice(null);
      resetForm();
      toast.success("Announcement updated successfully!");
    },
    onError: () => {
      toast.error("Failed to update announcement");
    },
  });

  // Delete notice mutation
  const deleteNoticeMutation = useMutation({
    mutationFn: deleteNotice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notices"] });
      toast.success("Announcement deleted successfully!");
    },
    onError: () => {
      toast.error("Failed to delete announcement");
    },
  });

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: markNoticeAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notices"] });
    },
    onError: () => {
      toast.error("Failed to mark announcement as read");
    },
  });

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
      type: "general",
      priority: "medium",
      targetAudience: ["all"],
      publishDate: new Date().toISOString().split("T")[0],
      expiryDate: "",
    });
  };

  const handleCreate = () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    createNoticeMutation.mutate(formData);
  };

  const handleUpdate = () => {
    if (!selectedNotice || !formData.title.trim() || !formData.content.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    updateNoticeMutation.mutate({ id: selectedNotice._id, data: formData });
  };

  const handleViewNotice = (notice: Notice) => {
    setSelectedNotice(notice);
    setShowViewModal(true);
  };

  const handleEdit = (notice: Notice) => {
    setSelectedNotice(notice);
    setFormData({
      title: notice.title,
      content: notice.content,
      type: notice.type,
      priority: notice.priority,
      targetAudience: notice.targetAudience,
      publishDate: notice.publishDate?.split("T")[0] || "",
      expiryDate: notice.expiryDate?.split("T")[0] || "",
    });
    setShowEditModal(true);
  };

  const handleDelete = (noticeId: string) => {
    if (window.confirm("Are you sure you want to delete this announcement?")) {
      deleteNoticeMutation.mutate(noticeId);
    }
  };

  const handleMarkAsRead = (noticeId: string) => {
    markAsReadMutation.mutate(noticeId);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "text-red-600 bg-red-50";
      case "medium":
        return "text-yellow-600 bg-yellow-50";
      case "low":
        return "text-green-600 bg-green-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "urgent":
        return "text-red-600 bg-red-50";
      case "important":
        return "text-orange-600 bg-orange-50";
      case "academic":
        return "text-blue-600 bg-blue-50";
      case "info":
        return "text-indigo-600 bg-indigo-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getAudienceIcon = (audience: string) => {
    switch (audience) {
      case "all":
        return <Globe className="w-4 h-4" />;
      case "students":
        return <Users className="w-4 h-4" />;
      case "teachers":
        return <Users className="w-4 h-4" />;
      case "staff":
        return <Building className="w-4 h-4" />;
      case "parents":
        return <Users className="w-4 h-4" />;
      default:
        return <Users className="w-4 h-4" />;
    }
  };

  return (
    <div className="p-6 min-h-screen bg-gradient-to-br from-gray-50 to-purple-50/30">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                System Announcements
              </h1>
              <p className="text-gray-600 mt-1">
                Manage announcements across all branches and user groups
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white px-6 py-3 rounded-xl flex items-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1 border-0"
            >
              <Plus className="w-5 h-5" />
              Create Announcement
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border-0">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Notices
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {pagination?.totalItems || validNotices.length}
                </p>
              </div>
              <Bell className="w-8 h-8 text-purple-500" />
            </div>
          </div>
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border-0">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Active Notices
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {validNotices.filter((n) => n.isActive).length}
                </p>
              </div>
              <Megaphone className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border-0">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  High Priority
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {validNotices.filter((n) => n.priority === "high").length}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
          </div>
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border-0">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">System-wide</p>
                <p className="text-2xl font-bold text-gray-900">
                  {
                    validNotices.filter((n) =>
                      Array.isArray(n.targetAudience)
                        ? n.targetAudience.includes("all")
                        : n.targetAudience === "all",
                    ).length
                  }
                </p>
              </div>
              <Globe className="w-8 h-8 text-blue-500" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border-0 mb-8">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <span className="font-medium text-gray-700">Filters:</span>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-600">Type:</label>
              <select
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="urgent">Urgent</option>
                <option value="important">Important</option>
                <option value="academic">Academic</option>
                <option value="info">Information</option>
                <option value="general">General</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-600">
                Audience:
              </label>
              <select
                value={filterAudience}
                onChange={(e) => {
                  setFilterAudience(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">All Audiences</option>
                <option value="all">System-wide</option>
                <option value="students">Students</option>
                <option value="teachers">Teachers</option>
                <option value="staff">Staff</option>
                <option value="parents">Parents</option>
              </select>
            </div>
          </div>
        </div>

        {/* Notices List */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border-0">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">
              All Announcements (
              {pagination?.totalItems || filteredNotices.length})
            </h2>
          </div>

          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading announcements...</p>
            </div>
          ) : filteredNotices.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {validNotices.length === 0
                  ? "No announcements yet"
                  : "No announcements match your filters"}
              </h3>
              <p className="text-gray-600 mb-4">
                {validNotices.length === 0
                  ? "Create your first system-wide announcement to get started"
                  : "Try adjusting your filters to see more results"}
              </p>
              {validNotices.length === 0 && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Create Announcement
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredNotices.map((notice) => (
                <div
                  key={notice._id}
                  className={`p-6 hover:bg-gray-50/50 transition-colors border-l-4 ${
                    !notice.isRead
                      ? "border-l-blue-500 bg-blue-50/30"
                      : "border-l-gray-300"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3
                          className={`text-lg font-semibold ${
                            !notice.isRead ? "text-gray-900" : "text-gray-700"
                          }`}
                        >
                          {notice.title}
                        </h3>
                        {!notice.isRead && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        )}
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(
                            notice.type,
                          )}`}
                        >
                          {notice.type}
                        </span>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(
                            notice.priority,
                          )}`}
                        >
                          {notice.priority}
                        </span>
                        {!notice.isActive && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full text-gray-600 bg-gray-100">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div
                        onClick={() => handleViewNotice(notice)}
                        className="cursor-pointer hover:bg-gray-100/50 -mx-2 px-2 py-2 rounded-lg transition-colors"
                      >
                        <p className="text-gray-600 mb-1 line-clamp-2">
                          {notice.content}
                        </p>
                        <span className="text-xs text-purple-600 hover:text-purple-700 font-semibold flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" />
                          Click to view full content
                        </span>
                      </div>
                      <div className="flex items-center gap-6 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          {getAudienceIcon(
                            Array.isArray(notice.targetAudience)
                              ? notice.targetAudience[0]
                              : notice.targetAudience,
                          )}
                          <span className="capitalize">
                            {Array.isArray(notice.targetAudience)
                              ? notice.targetAudience.join(", ")
                              : notice.targetAudience}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(notice.publishDate).toLocaleDateString()}
                        </div>
                        {notice.expiryDate && (
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            Expires:{" "}
                            {new Date(notice.expiryDate).toLocaleDateString()}
                          </div>
                        )}
                        {notice.author && (
                          <div className="text-xs text-gray-400">
                            By: {notice.author.name}
                            {(() => {
                              const authorUserId =
                                typeof notice.author.userId === "string"
                                  ? notice.author.userId
                                  : notice.author.userId._id;
                              return (
                                authorUserId === currentUser?.id && (
                                  <span className="ml-1 text-purple-600 font-medium">
                                    (You)
                                  </span>
                                )
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {!notice.isRead &&
                        (() => {
                          const authorUserId =
                            typeof notice.author.userId === "string"
                              ? notice.author.userId
                              : notice.author.userId._id;
                          return authorUserId !== currentUser?.id;
                        })() && (
                          <button
                            onClick={() => handleMarkAsRead(notice._id)}
                            disabled={markAsReadMutation.isPending}
                            className="px-3 py-1 bg-purple-600 text-white rounded-md text-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 mr-2"
                          >
                            {markAsReadMutation.isPending
                              ? "Marking..."
                              : "Mark as Read"}
                          </button>
                        )}
                      <button
                        onClick={() => handleEdit(notice)}
                        className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        title="Edit announcement"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(notice._id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete announcement"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && (
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.totalPages}
              onPageChange={setCurrentPage}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                Create System Announcement
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter announcement title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content *
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) =>
                    setFormData({ ...formData, content: e.target.value })
                  }
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter announcement content"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) =>
                      setFormData({ ...formData, type: e.target.value as any })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="general">General</option>
                    <option value="academic">Academic</option>
                    <option value="important">Important</option>
                    <option value="urgent">Urgent</option>
                    <option value="info">Information</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        priority: e.target.value as any,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Audience
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { value: "all", label: "All Users" },
                    { value: "students", label: "Students" },
                    { value: "teachers", label: "Teachers" },
                    { value: "staff", label: "Staff" },
                    { value: "parents", label: "Parents" },
                  ].map((audience) => (
                    <label
                      key={audience.value}
                      className="flex items-center gap-2 cursor-pointer hover:bg-purple-50 p-2 rounded-lg transition-colors border border-gray-200"
                    >
                      <input
                        type="checkbox"
                        checked={
                          formData.targetAudience?.includes(
                            audience.value as any,
                          ) || false
                        }
                        onChange={(e) => {
                          const currentAudiences =
                            formData.targetAudience || [];
                          let newAudiences: typeof formData.targetAudience;

                          if (e.target.checked) {
                            // If selecting "all", clear other selections
                            if (audience.value === "all") {
                              newAudiences = ["all"];
                            } else {
                              // Remove "all" if present and add new selection
                              newAudiences = [
                                ...currentAudiences.filter((a) => a !== "all"),
                                audience.value as any,
                              ];
                            }
                          } else {
                            // Remove the unchecked audience
                            newAudiences = currentAudiences.filter(
                              (a) => a !== audience.value,
                            );
                            // If no audiences selected, default to "all"
                            if (newAudiences.length === 0) {
                              newAudiences = ["all"];
                            }
                          }

                          setFormData({
                            ...formData,
                            targetAudience: newAudiences,
                          });
                        }}
                        className="w-4 h-4 text-purple-500 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                      />
                      <span className="text-gray-700 font-medium text-sm">
                        {audience.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Publish Date
                  </label>
                  <input
                    type="date"
                    value={formData.publishDate}
                    onChange={(e) =>
                      setFormData({ ...formData, publishDate: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiry Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) =>
                      setFormData({ ...formData, expiryDate: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={createNoticeMutation.isPending}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {createNoticeMutation.isPending
                  ? "Creating..."
                  : "Create Announcement"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedNotice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                Edit System Announcement
              </h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedNotice(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter announcement title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content *
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) =>
                    setFormData({ ...formData, content: e.target.value })
                  }
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter announcement content"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) =>
                      setFormData({ ...formData, type: e.target.value as any })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="general">General</option>
                    <option value="academic">Academic</option>
                    <option value="important">Important</option>
                    <option value="urgent">Urgent</option>
                    <option value="info">Information</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        priority: e.target.value as any,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Audience
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { value: "all", label: "All Users" },
                    { value: "students", label: "Students" },
                    { value: "teachers", label: "Teachers" },
                    { value: "staff", label: "Staff" },
                    { value: "parents", label: "Parents" },
                  ].map((audience) => (
                    <label
                      key={audience.value}
                      className="flex items-center gap-2 cursor-pointer hover:bg-purple-50 p-2 rounded-lg transition-colors border border-gray-200"
                    >
                      <input
                        type="checkbox"
                        checked={
                          formData.targetAudience?.includes(
                            audience.value as any,
                          ) || false
                        }
                        onChange={(e) => {
                          const currentAudiences =
                            formData.targetAudience || [];
                          let newAudiences: typeof formData.targetAudience;

                          if (e.target.checked) {
                            // If selecting "all", clear other selections
                            if (audience.value === "all") {
                              newAudiences = ["all"];
                            } else {
                              // Remove "all" if present and add new selection
                              newAudiences = [
                                ...currentAudiences.filter((a) => a !== "all"),
                                audience.value as any,
                              ];
                            }
                          } else {
                            // Remove the unchecked audience
                            newAudiences = currentAudiences.filter(
                              (a) => a !== audience.value,
                            );
                            // If no audiences selected, default to "all"
                            if (newAudiences.length === 0) {
                              newAudiences = ["all"];
                            }
                          }

                          setFormData({
                            ...formData,
                            targetAudience: newAudiences,
                          });
                        }}
                        className="w-4 h-4 text-purple-500 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
                      />
                      <span className="text-gray-700 font-medium text-sm">
                        {audience.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Publish Date
                  </label>
                  <input
                    type="date"
                    value={formData.publishDate}
                    onChange={(e) =>
                      setFormData({ ...formData, publishDate: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiry Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) =>
                      setFormData({ ...formData, expiryDate: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedNotice(null);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                disabled={updateNoticeMutation.isPending}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {updateNoticeMutation.isPending
                  ? "Updating..."
                  : "Update Announcement"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Announcement Modal */}
      {showViewModal && selectedNotice && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-purple-700 p-6 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                  <Eye className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white">
                  Announcement Details
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedNotice(null);
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Title */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {selectedNotice.title}
                </h3>
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`px-3 py-1 text-xs rounded-full ${getTypeColor(
                      selectedNotice.type,
                    )}`}
                  >
                    {selectedNotice.type}
                  </span>
                  <span
                    className={`px-3 py-1 text-xs rounded-full ${getPriorityColor(
                      selectedNotice.priority,
                    )}`}
                  >
                    {selectedNotice.priority}
                  </span>
                  {!selectedNotice.isActive && (
                    <span className="px-3 py-1 text-xs font-medium rounded-full text-gray-600 bg-gray-100">
                      Inactive
                    </span>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="bg-gray-50 p-6 rounded-xl">
                <h4 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">
                  Content
                </h4>
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {selectedNotice.content}
                </p>
              </div>

              {/* Metadata */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    {getAudienceIcon(
                      Array.isArray(selectedNotice.targetAudience)
                        ? selectedNotice.targetAudience[0]
                        : selectedNotice.targetAudience,
                    )}
                    <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Target Audience
                    </span>
                  </div>
                  <p className="text-gray-900 font-semibold capitalize">
                    {Array.isArray(selectedNotice.targetAudience)
                      ? selectedNotice.targetAudience.join(", ")
                      : selectedNotice.targetAudience}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Published Date
                    </span>
                  </div>
                  <p className="text-gray-900 font-semibold">
                    {new Date(selectedNotice.publishDate).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      },
                    )}
                  </p>
                </div>

                {selectedNotice.expiryDate && (
                  <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-amber-600" />
                      <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Expiry Date
                      </span>
                    </div>
                    <p className="text-gray-900 font-semibold">
                      {new Date(selectedNotice.expiryDate).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        },
                      )}
                    </p>
                  </div>
                )}

                {selectedNotice.author && (
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-4 h-4 text-purple-600" />
                      <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Author
                      </span>
                    </div>
                    <p className="text-gray-900 font-semibold">
                      {selectedNotice.author.name}
                    </p>
                    <p className="text-gray-600 text-sm capitalize">
                      {selectedNotice.author.department}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedNotice(null);
                }}
                className="px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAnnouncements;
